import { useState } from "react";
import { api, type QueueCard } from "../api";
import { KeeperBadge } from "./KeeperMark";
import { money, shortTime } from "../format";

export type CardAction =
  | "approve"
  | "reject"
  | "snooze"
  | "view"
  | "details"
  | "log"
  | "undo"
  | "restore";

export type CardKind = "proposal" | "insight" | "alert" | "gate" | "policy";
export type CardState = "pending" | "approved" | "rejected" | "snoozed" | "expired";

const KIND: Record<CardKind, { label: string; color: string }> = {
  proposal: { label: "Proposal", color: "var(--keeper)" },
  insight: { label: "Insight", color: "var(--text-4)" },
  alert: { label: "Alert", color: "var(--neg)" },
  gate: { label: "Gate result", color: "var(--pos)" },
  policy: { label: "Policy block", color: "var(--text-4)" },
};

const STATE_CHIP: Partial<Record<CardState, { label: string; color: string; bg: string }>> = {
  pending: { label: "Pending", color: "var(--warn)", bg: "var(--warn-bg)" },
  snoozed: { label: "Snoozed", color: "var(--warn)", bg: "var(--warn-bg)" },
  expired: { label: "Expired", color: "var(--text-4)", bg: "var(--muted-bg)" },
};

/** The shape the Card renders. Built from a QueueCard by `toCardView`. */
export type CardView = {
  id: string;
  kind: CardKind;
  state: CardState;
  ai: boolean;
  headline: string;
  number: string;
  diff?: string;
  impact?: string;
  details?: string;
  attrib?: string;
  until?: string;
  verdict?: "pass" | "fail";
  productSlug?: string | null;
  productName?: string | null;
  growthActionId?: string;
};

export function toCardView(c: QueueCard, currency = "USD"): CardView {
  const kind: CardKind =
    c.card_type === "gate_result" ? "gate" : c.card_type === "policy_block" ? "policy" : c.card_type;

  const expired = !!c.expires_at && new Date(c.expires_at).getTime() < Date.now();
  const state: CardState =
    c.status === "approved" || c.status === "executed"
      ? "approved"
      : c.status === "rejected" || c.status === "failed"
        ? "rejected"
        : c.status === "snoozed"
          ? "snoozed"
          : expired || c.status === "expired"
            ? "expired"
            : "pending";

  const spec = c.action_spec ?? {};
  const diff =
    spec.op === "set_budget" && typeof spec.from_cents === "number" && typeof spec.to_cents === "number"
      ? `${money(spec.from_cents, currency)}/day → ${money(spec.to_cents, currency)}/day`
      : undefined;
  const growthImpact =
    spec.op === "growth_action" && typeof spec.revenue_impact_cents === "number"
      ? `Est. ${money(spec.revenue_impact_cents, currency)}/mo`
      : typeof spec.expected_impact === "string" ? spec.expected_impact : undefined;

  const verb =
    state === "approved" ? "Approved" : state === "rejected" ? "Dismissed" : undefined;

  return {
    id: c.id,
    kind,
    state,
    ai: c.proposed_by === "keeper",
    headline: c.headline,
    number: c.metric_line,
    diff,
    impact: growthImpact,
    details: c.detail ?? undefined,
    attrib:
      verb && c.resolved_by ? `${verb} by ${c.resolved_by} · ${shortTime(c.resolved_at)}` : undefined,
    until: c.snoozed_until ? shortTime(c.snoozed_until) : undefined,
    productSlug: c.product_slug,
    productName: c.product_name,
    growthActionId: spec.op === "growth_action" && typeof spec.action_id === "string" ? spec.action_id : undefined,
  };
}

type Btn = {
  label: string;
  variant: "accent" | "solid" | "ghost" | "text";
  action: CardAction;
  spacerAfter?: boolean;
  disabled?: boolean;
  title?: string;
};

export function Card({
  card,
  onAct,
  busy,
}: {
  card: CardView;
  onAct: (id: string, action: CardAction) => void;
  busy?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [copied, setCopied] = useState(false);

  // Approved and rejected cards collapse to a one-line receipt.
  if (card.state === "approved" || card.state === "rejected") {
    return (
      <div className="card">
        <div className="card-receipt">
          <span
            style={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              flex: "none",
              background: card.state === "approved" ? "var(--pos)" : "var(--border-hover)",
            }}
          />
          <span className="card-receipt-text">{card.headline}</span>
          <span className="card-attrib num" style={{ whiteSpace: "nowrap" }}>
            {card.attrib}
          </span>
        </div>
      </div>
    );
  }

  const k = KIND[card.kind];
  const chip = STATE_CHIP[card.state];
  const chipLabel =
    card.state === "snoozed" && card.until ? `Snoozed · ${card.until}` : chip?.label;

  const numberColor =
    card.kind === "alert"
      ? "var(--neg)"
      : card.kind === "gate"
        ? card.verdict === "fail"
          ? "var(--neg)"
          : "var(--pos)"
        : card.kind === "policy"
          ? "var(--text-3)"
          : "var(--text)";

  const buttons = actionsFor(card, confirming);

  const run = (b: Btn) => {
    if (b.disabled) return;
    if (b.action === "details") {
      setExpanded((v) => !v);
      return;
    }
    // Approve spends money: first tap arms, second commits.
    if (b.action === "approve" && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    onAct(card.id, b.action);
  };

  return (
    <div className="card">
      {card.kind === "policy" && <div className="card-wall" aria-hidden="true" />}
      <div className="card-body" style={card.kind === "policy" ? { paddingLeft: 26 } : undefined}>
        <div className="card-top">
          <span className="card-kind" style={{ color: k.color }}>
            {k.label}
          </span>
          <span className="card-spacer" />
          {card.ai && <KeeperBadge />}
          {chip && (
            <span className="card-state" style={{ color: chip.color, background: chip.bg }}>
              {chipLabel}
            </span>
          )}
        </div>

        {/* Physically capped at three lines. Everything else lives behind Details. */}
        <div
          className="card-headline"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {card.headline}
        </div>

        <div className="card-number num" style={{ color: numberColor }}>
          {card.number}
        </div>

        {(card.diff || card.impact) && (
          <div className="card-diff">
            {card.diff && <span className="card-diff-pill num">{card.diff}</span>}
            {card.impact && <span className="card-impact">{card.impact}</span>}
          </div>
        )}

        {expanded && (
          <div className="card-details">
            {card.details ?? "No further detail was recorded for this card."}
          </div>
        )}

        <div className="card-actions">
          {buttons.map((b, i) => (
            <span key={`${b.action}-${i}`} style={{ display: "contents" }}>
              <button
                type="button"
                className={`btn-${b.variant}`}
                onClick={() => run(b)}
                disabled={b.disabled || busy}
                title={b.title}
                aria-expanded={b.action === "details" ? expanded : undefined}
              >
                {b.label}
              </button>
              {b.spacerAfter && <span className="card-spacer" />}
            </span>
          ))}
          {card.growthActionId && card.productSlug && (
            <button
              type="button"
              className="btn-ghost"
              disabled={busy}
              onClick={async () => {
                try {
                  const r = await api.growthPrompt(card.productSlug!, card.growthActionId!);
                  await navigator.clipboard.writeText(r.prompt);
                  setCopied(true);
                  window.setTimeout(() => setCopied(false), 1800);
                } catch {
                  /* clipboard / network — leave the label */
                }
              }}
            >
              {copied ? "Copied" : "Copy prompt"}
            </button>
          )}
        </div>

        {confirming && (
          <div className="card-attrib" style={{ color: "var(--keeper)" }}>
            Tap Confirm to commit. This changes spend.
          </div>
        )}
      </div>
    </div>
  );
}

function actionsFor(card: CardView, confirming: boolean): Btn[] {
  // Gate decisions write to the `gates` table, which has no HTTP route in P0 —
  // they're logged from the CLI. The control stays in the design, visibly off,
  // rather than silently doing nothing.
  const logDecision: Btn = {
    label: "Log decision",
    variant: "ghost",
    action: "log",
    disabled: true,
    title: "P0: gate decisions are logged from the CLI",
  };

  if (card.state === "snoozed" || card.state === "expired") {
    return [
      { label: "Details", variant: "ghost", action: "details", spacerAfter: true },
      { label: "Restore", variant: "text", action: "restore" },
    ];
  }

  switch (card.kind) {
    case "proposal":
      return [
        {
          label: confirming ? `Confirm ${card.diff ?? ""}`.trim() : "Approve",
          variant: "accent",
          action: "approve",
          spacerAfter: true,
        },
        { label: "Details", variant: "ghost", action: "details" },
        { label: "Dismiss", variant: "ghost", action: "reject" },
        { label: "Snooze", variant: "text", action: "snooze" },
      ];
    case "insight":
      return [
        { label: "Got it", variant: "solid", action: "reject", spacerAfter: true },
        { label: "Details", variant: "ghost", action: "details" },
        { label: "Snooze", variant: "text", action: "snooze" },
      ];
    case "alert":
      return [
        { label: "View", variant: "solid", action: "view", spacerAfter: true },
        { label: "Details", variant: "ghost", action: "details" },
        { label: "Snooze", variant: "text", action: "snooze" },
      ];
    case "gate":
      return [
        { label: "View", variant: "solid", action: "view", spacerAfter: true },
        { label: "Details", variant: "ghost", action: "details" },
        logDecision,
      ];
    case "policy":
      // No approve action exists on a policy block. By design, not by omission.
      return [
        { label: "Why blocked", variant: "ghost", action: "details", spacerAfter: true },
        logDecision,
      ];
  }
}
